import { createReducer } from '@reduxjs/toolkit'
import produce from 'immer'
import { fromPairs } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import flow from 'lodash/fp/flow'
import set from 'lodash/fp/set'
import update from 'lodash/fp/update'
import isEmpty from 'lodash/isEmpty'
import mapValues from 'lodash/mapValues'
import without from 'lodash/without'
import { normalize } from 'normalizr'
import { Layout } from 'react-grid-layout'
import { v4 as uuid } from 'uuid'

import {
    // Tab
    updateTabs,

    // widget
    addWidget,
    toggleWidgetModal,
    deleteWidget,
    updateWidget,
    cloneWidget,

    // Save Dashboard
    saveDashboardSuccess,
    saveDashboardFailure,
    saveDashboardRequest,
    handleFieldsChange,

    // dashboard actions
    changeLayout,
    fetchScheduledExportsSuccess,
    changeDashboardCurrencyCode,

    // Share
    shareDashboardRequest,
    shareDashboardSuccess,
    shareDashboardFailure,
} from 'actions/ui/dashboardPage'
import {
    updateWidgetPagination,
    updateWidgetSorter,
    updateWidgetTableColumns,
} from 'actions/ui/shared/dashboard'
import { Y_BOTTOM_OF_PAGE } from 'const/dashboards'
import { DATES, isFilterValueTruthy } from 'const/filters'
import { FILTER_SETTINGS } from 'const/reducerKeys'
import { getCurrentTab } from 'routing/pagePaths'
import { tabListSchema } from 'schemas'
import { DashboardPageState, DashboardTab } from 'types'

import {
    defaultWidgetState,
    defaultPaginationAndSorter,
    defaultWidgetLayout,
    defaultDashboardPage,
    defaultDatesFilter,
    AVAILABLE_TAB_FILTERS,
} from '../defaults'

const defaultState: DashboardPageState = {
    ...defaultDashboardPage,
    widgetModalOpen: false,
    saving: false,
    isDirty: false,
    scheduledExport: null,
}

export default createReducer(
    cloneDeep(defaultState), // create clone, so the defaultState is not mutated
    (builder) => {
        builder
            // add widget
            .addCase(addWidget, (state, action) => {
                const widget = action.payload
                const tabId = getCurrentTab()
                if (!tabId) {
                    return state
                }
                return flow(
                    update(['tabs', tabId, 'widgets'], (widgets) => [
                        ...widgets,
                        widget.id,
                    ]),
                    set(['widgets', widget.id], {
                        ...widget,
                        ...defaultWidgetState,
                        ...defaultPaginationAndSorter(widget),
                        layout: widget.layout?.h
                            ? {
                                  ...widget.layout, // width and height of source widget
                                  i: widget.id,
                                  x: 0, // left aligned
                                  y: Y_BOTTOM_OF_PAGE,
                              }
                            : defaultWidgetLayout(widget, Y_BOTTOM_OF_PAGE)
                                  .layout,
                        new: true,
                    }),
                    set('widgetModalOpen', false),
                    set('isDirty', true)
                )(state)
            })

            .addCase(cloneWidget, (state, action) => {
                const { tabId, widgetId } = action.payload
                if (!tabId) {
                    return state
                }
                const sourceWidget = state.widgets[widgetId]
                const newWidgetId = uuid()

                return flow(
                    update(['tabs', tabId, 'widgets'], (widgets: string[]) => [
                        ...widgets,
                        newWidgetId,
                    ]),
                    set(['widgets', newWidgetId], {
                        ...sourceWidget,
                        id: newWidgetId,
                        name: sourceWidget.name
                            ? `${sourceWidget.name} (copy)`
                            : '',
                        new: true,
                        layout: {
                            ...sourceWidget.layout, // width and height of source widget
                            i: newWidgetId,
                            x: 0, // left aligned
                            y: Y_BOTTOM_OF_PAGE,
                        },
                    }),
                    set('isDirty', true)
                )(state)
            })

            .addCase(toggleWidgetModal, (state) =>
                set('widgetModalOpen', !state.widgetModalOpen, state)
            )

            // delete widget
            .addCase(deleteWidget, (state, action) => {
                const { tabId, widgetId } = action.payload
                return flow(
                    set(
                        ['tabs', tabId, 'widgets'],
                        without(state.tabs[tabId].widgets, widgetId)
                    ),
                    set(
                        ['widgets'],
                        produce(state.widgets, (draft) => {
                            delete draft[widgetId]
                        })
                    ),
                    set('isDirty', true)
                )(state)
            })

            // update widget
            .addCase(updateWidget, (state, action) => {
                const widget = action.payload
                return flow(
                    set(['widgets', widget.id], {
                        ...widget,
                        ...defaultPaginationAndSorter(widget),
                    }),
                    set('widgetModalOpen', false),
                    set('isDirty', true)
                )(state)
            })

            .addCase(updateTabs, (state, action) => {
                const {
                    entities: { tabs, widgets },
                } = normalize(action.payload, tabListSchema)

                return flow(
                    set(
                        'tabs',
                        mapValues(tabs, (tab: DashboardTab) =>
                            produce(tab, (draft: any) => {
                                draft.localFilters = {
                                    ...fromPairs(
                                        AVAILABLE_TAB_FILTERS.map((filter) => [
                                            filter,
                                            undefined,
                                        ])
                                    ),
                                    ...draft.localFilters,
                                    [DATES]: !isEmpty(
                                        draft?.localFilters?.[DATES]
                                    )
                                        ? draft.localFilters?.[DATES]
                                        : defaultDatesFilter,
                                }
                                const userAddedFilters =
                                    AVAILABLE_TAB_FILTERS.filter(
                                        (filterKey) => {
                                            if (filterKey === DATES) {
                                                return (
                                                    draft.localFilters?.[
                                                        DATES
                                                    ] !== defaultDatesFilter
                                                )
                                            }
                                            return isFilterValueTruthy(
                                                draft.localFilters?.[filterKey]
                                            )
                                        }
                                    )
                                draft[FILTER_SETTINGS] = {
                                    anchored: [],
                                    order: AVAILABLE_TAB_FILTERS,
                                    userAdded: userAddedFilters,
                                    // no clear button in the home page
                                    touched: userAddedFilters.length > 0,
                                    displayState: {},
                                }
                            })
                        )
                    ),
                    set('widgets', widgets),
                    set('isDirty', true)
                )(state)
            })

            // save dashboard
            .addCase(saveDashboardRequest, (state) =>
                set('saving', true, state)
            )
            .addCase(saveDashboardSuccess, (state, action) => {
                const { payload: dashboard } = action

                return flow(
                    set('saving', false),
                    set('isDirty', false),
                    set('dashboard', {
                        ...state.dashboard,
                        ...dashboard,
                    })
                )(state)
            })
            .addCase(saveDashboardFailure, (state, action) => {
                const { message } = action.payload
                return flow(set('saving', false), set('error', message))(state)
            })

            // share dashboard
            .addCase(shareDashboardRequest, (state) =>
                set('saving', true, state)
            )
            .addCase(shareDashboardSuccess, (state) =>
                set('saving', false, state)
            )
            .addCase(shareDashboardFailure, (state, action) => {
                const { message } = action.payload
                return flow(set('saving', false), set('error', message))(state)
            })

            // handle fields change
            .addCase(handleFieldsChange, (state, action) => {
                const { name, description, markAsDirty } = action.payload
                return flow(
                    set(['dashboard', 'name'], name),
                    set(['dashboard', 'description'], description),
                    set('isDirty', markAsDirty || state.isDirty)
                )(state)
            })

            .addCase(changeLayout, (state, action) => {
                const layouts = action.payload
                return flow(
                    update(['widgets'], (widgets) => {
                        const widgetsToUpdate = cloneDeep(widgets)

                        layouts.forEach((layout: Layout) => {
                            const widget = widgetsToUpdate[layout.i]

                            if (widget) {
                                widget.layout = layout
                            }
                        })

                        return widgetsToUpdate
                    }),
                    set(['isDirty'], true)
                )(state)
            })

            .addCase(fetchScheduledExportsSuccess, (state, action) => {
                const scheduledExports = action.payload.results
                return set(
                    ['scheduledExport'],
                    (scheduledExports?.length ?? 0) > 0
                        ? scheduledExports[0]
                        : null
                )(state)
            })

            .addCase(updateWidgetPagination, (state, action) => {
                const {
                    pageName,
                    data: { widgetId, pagination },
                } = action.payload

                if (pageName !== 'dashboardPage') {
                    return
                }

                const widget = state.widgets[widgetId]
                if (widget.limit !== pagination.pageSize) {
                    widget.limit = pagination.pageSize
                    state.isDirty = true
                }
            })

            .addCase(updateWidgetSorter, (state, action) => {
                const {
                    pageName,
                    data: { widgetId, sorter },
                } = action.payload

                if (pageName !== 'dashboardPage') {
                    return
                }

                const widget = state.widgets[widgetId]
                if (widget.order_by !== sorter.field) {
                    widget.order_by = sorter.field as string
                    state.isDirty = true
                }
                if (widget.order_by_direction !== sorter.order) {
                    widget.order_by_direction = sorter.order
                    state.isDirty = true
                }
            })

            .addCase(updateWidgetTableColumns, (state, action) => {
                const { pageName } = action.payload

                if (pageName === 'dashboardPage') {
                    return set(['isDirty'], true)(state)
                }

                return state
            })

            .addCase(changeDashboardCurrencyCode, (state, action) => {
                const currencyCode = action.payload

                return flow(
                    set('currencyCode', currencyCode),
                    set('isDirty', true),
                    update('tabs', (tabs) =>
                        mapValues(tabs, (tab) => ({
                            ...tab,
                            loaded: false,
                        }))
                    )
                )(state)
            })
    }
)
